import { Link, useNavigate, useParams } from 'react-router-dom';
import { Icon } from '../../layout/Icon.jsx';
import { Pill } from '../../layout/Pill.jsx';
import { Avatar } from '../../layout/Avatar.jsx';
import { apiError } from '../../shared/api.js';
import { formatIQD, formatDateTime, formatDate } from '../../shared/format.js';
import { useSearchRecord } from './useGlobalSearch.js';
import {
  SESSION_STATUS_LABEL, SALES_STATUS_LABEL, PURCHASE_STATUS_LABEL, POS_TYPE_LABEL
} from './resultMeta.js';

const TYPE_META = {
  child: { label: 'طفل', icon: 'children' },
  guardian: { label: 'ولي أمر', icon: 'reception' },
  customer: { label: 'عميل', icon: 'finance' },
  invoice: { label: 'فاتورة مبيعات', icon: 'checkout' },
  product: { label: 'منتج', icon: 'products' },
  supplier: { label: 'مورد', icon: 'purchase' }
};

function resolvePhoto(url) {
  if (!url) return null;
  if (url.startsWith('http') || url.startsWith('/api/') || url.startsWith('/uploads/')) return url;
  return `/api/children/photos/${url.replace(/^\//, '')}`;
}

function Field({ label, children }) {
  if (children == null || children === '' || children === false) return null;
  return (
    <>
      <div className="text-ink-3 text-[12px]">{label}</div>
      <div className="text-[13px] font-semibold">{children}</div>
    </>
  );
}

function Grid({ children }) {
  return <div className="grid gap-y-2 gap-x-4" style={{ gridTemplateColumns: 'auto 1fr' }}>{children}</div>;
}

function ChildBody({ data }) {
  const c = data.child;
  return (
    <div className="space-y-3.5">
      <div className="card">
        <div className="card-b flex items-start gap-4">
          <Avatar src={resolvePhoto(c.photoUrl)} name={c.name} id={data.id} size="lg" />
          <div className="flex-1">
            <Grid>
              <Field label="الاسم">{c.name}</Field>
              <Field label="العمر">{c.age != null ? `${c.age} سنوات` : null}</Field>
              <Field label="الجنس">{c.gender === 'male' ? 'ذكر' : 'أنثى'}</Field>
              <Field label="الكارت">{c.cardUid}</Field>
              <Field label="عدد الزيارات">{c.visitsCount}</Field>
              <Field label="ملاحظات طبية">{c.medicalNotes}</Field>
              <Field label="حساسية">{c.allergyNotes}</Field>
            </Grid>
          </div>
        </div>
      </div>

      {data.guardian ? (
        <div className="card">
          <div className="card-h"><div className="font-bold text-[13px]">ولي الأمر</div></div>
          <div className="card-b flex items-center justify-between gap-3">
            <div>
              <div className="font-semibold text-[13px]">{data.guardian.name}</div>
              <div className="text-ink-3 text-[12px]" dir="ltr">{data.guardian.phone}</div>
            </div>
            <Link className="btn" to={`/search/guardian/${data.guardian.id}`}>فتح ولي الأمر</Link>
          </div>
        </div>
      ) : null}

      {data.siblings.length ? (
        <div className="card">
          <div className="card-h"><div className="font-bold text-[13px]">الإخوة</div></div>
          <div className="card-b flex flex-wrap gap-2">
            {data.siblings.map((s) => (
              <Link key={s.id} className="btn" to={`/search/child/${s.id}`}>{s.name}</Link>
            ))}
          </div>
        </div>
      ) : null}

      <div className="card">
        <div className="card-h">
          <div className="font-bold text-[13px]">آخر الجلسات</div>
          <div className="flex-1" />
          {data.activeSessionId ? (
            <Link className="btn btn-primary" to={`/checkout/${data.activeSessionId}`}>
              <Icon name="checkout" size={14} /> تسوية وخروج
            </Link>
          ) : null}
        </div>
        <div className="card-b">
          {data.sessions.length === 0 ? (
            <div className="text-ink-3 text-center py-3 text-[12.5px]">لا توجد جلسات</div>
          ) : (
            <table className="w-full text-[12.5px]">
              <thead className="text-ink-2 text-[11.5px]">
                <tr className="border-b border-line">
                  <th className="p-2 text-start">الحالة</th>
                  <th className="p-2 text-start">الدخول</th>
                  <th className="p-2 text-start">الخروج</th>
                  <th className="p-2 text-start">الكارت</th>
                  <th className="p-2 text-start">المبلغ</th>
                </tr>
              </thead>
              <tbody>
                {data.sessions.map((s) => (
                  <tr key={s.id} className="border-b border-line last:border-b-0">
                    <td className="p-2">
                      <Pill variant={s.status === 'active' ? 'success' : 'muted'}>
                        {SESSION_STATUS_LABEL[s.status] || s.status}
                      </Pill>
                    </td>
                    <td className="p-2">{formatDateTime(s.startTime)}</td>
                    <td className="p-2">{s.endTime ? formatDateTime(s.endTime) : '—'}</td>
                    <td className="p-2 font-mono" dir="ltr">{s.cardUid || '—'}</td>
                    <td className="p-2">{formatIQD(s.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function GuardianBody({ data }) {
  const g = data.guardian;
  return (
    <div className="space-y-3.5">
      <div className="card">
        <div className="card-b">
          <Grid>
            <Field label="الاسم">{g.name}</Field>
            <Field label="الهاتف"><span dir="ltr">{g.phone}</span></Field>
            <Field label="هاتف إضافي">{g.secondaryPhone ? <span dir="ltr">{g.secondaryPhone}</span> : null}</Field>
            <Field label="عدد الزيارات">{g.visitsCount}</Field>
            <Field label="ملاحظات">{g.notes}</Field>
          </Grid>
        </div>
      </div>

      <div className="card">
        <div className="card-h">
          <div className="font-bold text-[13px]">الأطفال ({data.children.length})</div>
          <div className="flex-1" />
          <Link className="btn" to={`/search/customer/${data.id}`}>
            <Icon name="finance" size={14} /> الحساب والديون
          </Link>
        </div>
        <div className="card-b">
          {data.children.length === 0 ? (
            <div className="text-ink-3 text-center py-3 text-[12.5px]">لا يوجد أطفال مسجّلون</div>
          ) : (
            <div className="flex flex-col gap-2">
              {data.children.map((c) => (
                <div key={c.id} className="flex items-center gap-2.5 border border-line rounded-[10px] px-3 py-2">
                  <Avatar src={resolvePhoto(c.photoUrl)} name={c.name} id={c.id} size="sm" />
                  <div className="flex-1">
                    <div className="font-semibold text-[13px]">{c.name}</div>
                    <div className="text-ink-3 text-[11.5px]">{c.age} سنوات</div>
                  </div>
                  {c.inHall ? <Pill variant="success">داخل القاعة</Pill> : null}
                  <Link className="btn" to={`/search/child/${c.id}`}>فتح</Link>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CustomerBody({ data }) {
  return (
    <div className="space-y-3.5">
      <div className="card">
        <div className="card-b">
          <Grid>
            <Field label="الاسم">{data.guardian.name}</Field>
            <Field label="الهاتف"><span dir="ltr">{data.guardian.phone}</span></Field>
            <Field label="إجمالي الفواتير">{formatIQD(data.totals.amount)}</Field>
            <Field label="المسدَّد">{formatIQD(data.totals.paid)}</Field>
            <Field label="المتبقي">
              <span className={data.totals.due > 0 ? 'text-red' : ''}>{formatIQD(data.totals.due)}</span>
            </Field>
          </Grid>
        </div>
      </div>

      <div className="card">
        <div className="card-h">
          <div className="font-bold text-[13px]">الفواتير</div>
          <div className="flex-1" />
          <Link className="btn" to="/debts"><Icon name="finance" size={14} /> شاشة ديون العملاء</Link>
        </div>
        <div className="card-b">
          {data.invoices.length === 0 ? (
            <div className="text-ink-3 text-center py-3 text-[12.5px]">لا توجد فواتير</div>
          ) : (
            <table className="w-full text-[12.5px]">
              <thead className="text-ink-2 text-[11.5px]">
                <tr className="border-b border-line">
                  <th className="p-2 text-start">الرقم</th>
                  <th className="p-2 text-start">النوع</th>
                  <th className="p-2 text-start">التاريخ</th>
                  <th className="p-2 text-start">الإجمالي</th>
                  <th className="p-2 text-start">المتبقي</th>
                  <th className="p-2 text-start">الحالة</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.invoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-line last:border-b-0">
                    <td className="p-2 font-mono" dir="ltr">{inv.number}</td>
                    <td className="p-2">{POS_TYPE_LABEL[inv.posType] || inv.posType}</td>
                    <td className="p-2">{formatDate(inv.date)}</td>
                    <td className="p-2">{formatIQD(inv.total)}</td>
                    <td className="p-2">{formatIQD(Math.max(0, inv.due))}</td>
                    <td className="p-2">
                      <Pill variant={inv.status === 'paid' ? 'success' : 'orange'}>
                        {SALES_STATUS_LABEL[inv.status] || inv.status}
                      </Pill>
                    </td>
                    <td className="p-2 text-end">
                      <Link className="btn" to={`/search/invoice/${inv.id}`}>تفاصيل</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function InvoiceBody({ data }) {
  const inv = data.invoice;
  return (
    <div className="space-y-3.5">
      <div className="card">
        <div className="card-b">
          <Grid>
            <Field label="رقم الفاتورة"><span dir="ltr" className="font-mono">{inv.number}</span></Field>
            <Field label="النوع">{POS_TYPE_LABEL[inv.posType] || inv.posType}</Field>
            <Field label="الحالة">
              <Pill variant={inv.status === 'paid' ? 'success' : 'orange'}>
                {SALES_STATUS_LABEL[inv.status] || inv.status}
              </Pill>
            </Field>
            <Field label="التاريخ">{formatDateTime(inv.createdAt)}</Field>
            <Field label="الإجمالي">{formatIQD(inv.grandTotal)}</Field>
            <Field label="المدفوع">{formatIQD(inv.paidAmount)}</Field>
            <Field label="المتبقي">{formatIQD(Math.max(0, inv.dueAmount))}</Field>
            <Field label="ملاحظات">{inv.notes}</Field>
          </Grid>
        </div>
      </div>

      {data.guardian || data.child ? (
        <div className="card">
          <div className="card-b flex flex-wrap items-center gap-2">
            {data.child ? (
              <Link className="btn" to={`/search/child/${data.child.id}`}>
                <Icon name="children" size={14} /> {data.child.name}
              </Link>
            ) : null}
            {data.guardian ? (
              <Link className="btn" to={`/search/customer/${data.guardian.id}`}>
                <Icon name="reception" size={14} /> {data.guardian.name}
              </Link>
            ) : null}
            {data.sessionId ? (
              <Link className="btn btn-primary" to={`/checkout/${data.sessionId}`}>
                <Icon name="checkout" size={14} /> فتح المحاسبة
              </Link>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="card">
        <div className="card-h"><div className="font-bold text-[13px]">البنود</div></div>
        <div className="card-b">
          {data.items.length === 0 ? (
            <div className="text-ink-3 text-center py-3 text-[12.5px]">لا توجد بنود</div>
          ) : (
            <table className="w-full text-[12.5px]">
              <thead className="text-ink-2 text-[11.5px]">
                <tr className="border-b border-line">
                  <th className="p-2 text-start">المنتج</th>
                  <th className="p-2 text-start">الكمية</th>
                  <th className="p-2 text-start">السعر</th>
                  <th className="p-2 text-start">الإجمالي</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((it, i) => (
                  <tr key={`${it.sku}-${i}`} className="border-b border-line last:border-b-0">
                    <td className="p-2">{it.name}</td>
                    <td className="p-2">{it.qty}</td>
                    <td className="p-2">{formatIQD(it.unitPrice)}</td>
                    <td className="p-2">{formatIQD(it.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function ProductBody({ data }) {
  const p = data.product;
  return (
    <div className="card">
      <div className="card-b">
        <Grid>
          <Field label="الاسم">{p.name}</Field>
          <Field label="الرمز"><span dir="ltr" className="font-mono">{p.sku}</span></Field>
          <Field label="الباركود">{p.barcode ? <span dir="ltr" className="font-mono">{p.barcode}</span> : null}</Field>
          <Field label="سعر البيع">{formatIQD(p.salePrice)}</Field>
          <Field label="سعر الكلفة">{p.costPrice != null ? formatIQD(p.costPrice) : null}</Field>
          <Field label="المخزون الحالي">{p.currentStock != null ? p.currentStock : 'بلا مخزون'}</Field>
          <Field label="حد التنبيه">{p.minStockAlert}</Field>
          <Field label="الحالة">
            <Pill variant={p.isActive ? 'success' : 'muted'}>{p.isActive ? 'مفعّل' : 'معطّل'}</Pill>
          </Field>
          <Field label="الوصف">{p.description}</Field>
        </Grid>
        <div className="mt-3.5">
          <Link className="btn" to="/products"><Icon name="products" size={14} /> شاشة المنتجات</Link>
        </div>
      </div>
    </div>
  );
}

function SupplierBody({ data }) {
  const s = data.supplier;
  return (
    <div className="space-y-3.5">
      <div className="card">
        <div className="card-b">
          <Grid>
            <Field label="الاسم">{s.name}</Field>
            <Field label="الهاتف">{s.phone ? <span dir="ltr">{s.phone}</span> : null}</Field>
            <Field label="البريد">{s.email}</Field>
            <Field label="الرصيد">{s.balance != null ? formatIQD(s.balance) : null}</Field>
            <Field label="ملاحظات">{s.notes}</Field>
          </Grid>
          <div className="mt-3.5">
            <Link className="btn" to="/purchase"><Icon name="purchase" size={14} /> شاشة المشتريات</Link>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-h"><div className="font-bold text-[13px]">فواتير المورد</div></div>
        <div className="card-b">
          {data.invoices.length === 0 ? (
            <div className="text-ink-3 text-center py-3 text-[12.5px]">لا توجد فواتير</div>
          ) : (
            <table className="w-full text-[12.5px]">
              <thead className="text-ink-2 text-[11.5px]">
                <tr className="border-b border-line">
                  <th className="p-2 text-start">الرقم</th>
                  <th className="p-2 text-start">التاريخ</th>
                  <th className="p-2 text-start">الإجمالي</th>
                  <th className="p-2 text-start">المتبقي</th>
                  <th className="p-2 text-start">الحالة</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.invoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-line last:border-b-0">
                    <td className="p-2 font-mono" dir="ltr">{inv.number}</td>
                    <td className="p-2">{formatDate(inv.date)}</td>
                    <td className="p-2">{formatIQD(inv.total)}</td>
                    <td className="p-2">{formatIQD(Math.max(0, inv.due))}</td>
                    <td className="p-2">
                      <Pill variant={inv.status === 'posted' ? 'success' : 'muted'}>
                        {PURCHASE_STATUS_LABEL[inv.status] || inv.status}
                      </Pill>
                    </td>
                    <td className="p-2 text-end">
                      <Link className="btn" to={`/purchase/${inv.id}`}>فتح</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

const BODY = {
  child: ChildBody,
  guardian: GuardianBody,
  customer: CustomerBody,
  invoice: InvoiceBody,
  product: ProductBody,
  supplier: SupplierBody
};

export default function SearchRecord() {
  const { type, id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, error } = useSearchRecord(type, id);
  const meta = TYPE_META[type];

  if (!meta) {
    return (
      <div className="card">
        <div className="card-b text-center text-ink-3 py-6">نوع سجل غير معروف</div>
      </div>
    );
  }

  const Body = BODY[type];

  return (
    <div className="space-y-3.5" data-testid="search-record">
      <div className="card">
        <div className="card-b flex items-center gap-3">
          <button type="button" className="btn" onClick={() => navigate(-1)} title="رجوع">
            <Icon name="chevronR" size={15} />
          </button>
          <Icon name={meta.icon} size={20} color="#FF7A1A" />
          <div>
            <div className="text-[11.5px] text-ink-3">{meta.label}</div>
            <h2 className="text-[17px] font-bold m-0" data-testid="search-record-title">
              {isLoading ? '...' : (data?.title || '—')}
            </h2>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="card"><div className="card-b text-center text-ink-3 py-8">جاري التحميل...</div></div>
      ) : error ? (
        <div className="card"><div className="card-b text-center text-red py-8">{apiError(error)}</div></div>
      ) : data ? (
        <Body data={data} />
      ) : null}
    </div>
  );
}
